import React, { useMemo } from 'react';
import ReactSelect, { SingleValue } from 'react-select';
import styled from 'styled-components';
import { SeatMapArea } from '@src/core/buyerApiClient';
import { useWidgetsTheme as theme } from '../../core/hooks';
import { MdArrowBack, MdArrowForward } from 'react-icons/md';
import { LayoutColors } from '@src/core/theme';

interface SeatMapAreaSelectorProps {
  areas: SeatMapArea[];
  selectedAreaId?: string | null;
  onChange: (areaId: string) => void;
}

export const SeatMapAreaSelector = (props: SeatMapAreaSelectorProps) => {
  const { areas, selectedAreaId, onChange } = props;

  const actionColor = theme('actionColor');

  const handleChange = (selectedValue: SingleValue<SeatMapArea>) => {
    const area = areas.find((area) => area.id === selectedValue?.id);
    if (area) {
      onChange(area.id);
    }
  };

  const currentIndex = useMemo(() => areas.findIndex((area) => area.id === selectedAreaId), [selectedAreaId, areas]);
  const selectedArea = areas[currentIndex];
  const hasNext = currentIndex < areas.length - 1;
  const hasPrevious = currentIndex > 0;

  const handlePrevious = () => {
    if (hasPrevious) {
      onChange(areas[currentIndex - 1]!.id);
    }
  };

  const handleNext = () => {
    if (hasNext) {
      onChange(areas[currentIndex + 1]!.id);
    }
  };

  return (
    <NavigationContainer>
      <NavigationButton color={actionColor} onClick={handlePrevious} disabled={!hasPrevious}>
        <MdArrowBack size={22} color={actionColor} />
      </NavigationButton>

      <ReactSelect
        options={areas}
        onChange={(value) => handleChange(value)}
        value={selectedArea}
        defaultValue={areas.find((area) => area.id === selectedArea?.id)}
        formatOptionLabel={(area: SeatMapArea) => area.name}
        styles={{
          control: (base) => ({
            ...base,
            border: `1px solid ${actionColor}}`,
            height: '35px',
            minHeight: '35px',
            boxShadow: '0px 3px 5px 0px rgba(0, 0, 0, 0.15)',
            cursor: 'pointer',
            '&:hover': {
              borderColor: actionColor,
            },
          }),
          container: (base) => ({
            ...base,
            width: '100%',
            maxWidth: '175px',
            margin: '0 8px',
          }),
          singleValue: (base) => ({
            ...base,
            color: actionColor,
            fontSize: '11px',
            cursor: 'pointer',
          }),
          dropdownIndicator: (base) => ({
            ...base,
            color: actionColor,
            padding: '7px',
            '&:hover': {
              color: actionColor,
            },
          }),
          option: (base) => ({
            ...base,
            fontSize: '11px',
            cursor: 'pointer',
            backgroundColor: 'white',
            color: LayoutColors.blackMain,

            '&:active': {
              backgroundColor: actionColor,
            },
          }),
        }}
        isSearchable={false}
        components={{
          IndicatorSeparator: () => null,
        }}
      />

      <NavigationButton color={actionColor} onClick={handleNext} disabled={!hasNext}>
        <MdArrowForward size={22} color={actionColor} />
      </NavigationButton>
    </NavigationContainer>
  );
};

export default SeatMapAreaSelector;

const NavigationContainer = styled.div`
  display: flex;
  align-items: center;
  max-width: 260px;
  width: 100%;
`;

interface NavigationButtonProps {
  color: string;
}

const NavigationButton = styled.button<NavigationButtonProps>`
  border: 1px solid ${({ color }) => color};
  height: 35px;
  width: 35px;
  min-width: 35px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  background-color: white;
  box-shadow: 0px 3px 5px 0px rgba(0, 0, 0, 0.15);

  &:disabled {
    opacity: 0.4;
  }
`;
